import { BookingCard, type BookingViewer } from "./BookingCard";
import { BookingsDaySectionHeader } from "./BookingsDaySectionHeader";
import { totalGuests } from "./bookingDayAgenda";
import { cn } from "@/lib/utils";
import type { BookingDaySection } from "./bookingCalendar";
import type { BookingListRow } from "./bookingViewModel";

interface BookingsDayGroupedListProps {
  sections: BookingDaySection[];
  layout: "row" | "grid";
  bookingsListPath: string;
  viewer: BookingViewer;
  processingByBookingId: Record<string, boolean>;
  fileInputResetKey: Record<string, number>;
  onReceiptUpload: (booking: BookingListRow, file?: File | null) => void;
  onManualAmountClick?: (bookingId: string, storagePath: string) => void;
  onEditSellerNotes?: (bookingId: string, currentNotes: string | null) => void;
}

export function BookingsDayGroupedList({
  sections,
  layout,
  bookingsListPath,
  viewer,
  processingByBookingId,
  fileInputResetKey,
  onReceiptUpload,
  onManualAmountClick,
  onEditSellerNotes,
}: BookingsDayGroupedListProps) {
  const isGrid = layout === "grid";

  return (
    <div className="space-y-8">
      {sections.map((section) => {
        const covers = totalGuests(section.bookings);

        return (
          <section
            key={section.dateKey ?? "undated"}
            className="space-y-3"
          >
            <BookingsDaySectionHeader
              dateKey={section.dateKey}
              bookingCount={section.bookings.length}
              covers={covers}
              idPrefix="bookings-day"
            />

            <div
              className={cn(
                isGrid
                  ? "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
                  : "space-y-3",
              )}
            >
              {section.bookings.map((booking) => {
                const latestUploadPath = booking.latestUploadPath;
                return (
                  <BookingCard
                    key={`${booking.type}-${booking.id}`}
                    booking={booking}
                    detailPath={`${bookingsListPath}/${booking.id}`}
                    viewer={viewer}
                    layout={layout}
                    omitVisitDate={section.dateKey != null}
                    isProcessingReceipt={
                      processingByBookingId[booking.id] ?? false
                    }
                    fileInputResetKey={fileInputResetKey[booking.id] ?? 0}
                    onReceiptUpload={(file) => onReceiptUpload(booking, file)}
                    onManualAmountClick={
                      onManualAmountClick && latestUploadPath
                        ? () =>
                            onManualAmountClick(booking.id, latestUploadPath)
                        : undefined
                    }
                    onEditSellerNotes={onEditSellerNotes}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
